import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import {DashboardLayout} from "components/Layout";
import {CircleLoader} from "components/Loader";
import {useToast} from "hooks/useToast";
import {InvoiceProductSection} from "components/invoice/InvoiceProductSection";
import {PaymentMethodFields} from "components/invoice/PaymentMethodFields";
import {
	getAdminPriceOptions,
	getDoctorOptions,
	getInvoiceDetail,
	getMedicineDetail,
	getMedicineOptions,
	getMedicinePreparationOptions,
	getMedicineUnitOptions,
	updateInvoice,
} from "services/invoiceService";
import {
	calculateGrandTotal,
	calculateSectionSubtotal,
	createInvoiceInitialFormState,
	getApiErrorMessage,
	INVOICE_PRODUCT_RELATION_FIELDS,
	mapFormStateToUpdatePayload,
	mapInvoiceDetailToFormState,
	toPositiveNumber,
} from "utils/invoiceMapper";

function formatCurrency(value) {
	return new Intl.NumberFormat("id-ID", {
		style: "currency",
		currency: "IDR",
		maximumFractionDigits: 0,
	}).format(Number(value) || 0);
}

function validateSectionItems(items = [], relationFields = []) {
	return items.map(item => {
		const sectionErrors = {};
		const hasTitle = Boolean((item?.title || "").trim());
		const hasQty = item?.qty !== "" && item?.qty !== null && item?.qty !== undefined;
		const hasPrice = item?.price !== "" && item?.price !== null && item?.price !== undefined;
		const hasRelationFieldValue = relationFields.some(
			relationField =>
				item?.[relationField.key] !== "" &&
				item?.[relationField.key] !== null &&
				item?.[relationField.key] !== undefined,
		);

		if (!hasTitle && !hasQty && !hasPrice && !hasRelationFieldValue) {
			return sectionErrors;
		}

		const qty = toPositiveNumber(item?.qty);
		const price = toPositiveNumber(item?.price);

		if (!qty || qty <= 0) {
			sectionErrors.qty = "Qty harus angka dan > 0";
		}

		if (!price || price <= 0) {
			sectionErrors.price = "Price harus angka dan > 0";
		}

		relationFields.forEach(relationField => {
			if (relationField?.nullable) {
				return;
			}

			if (!item?.[relationField.key] && item?.[relationField.key] !== 0) {
				sectionErrors[relationField.key] = `${relationField.label} wajib diisi`;
			}
		});

		return sectionErrors;
	});
}

function validateOptionalSectionItems(items = [], relationFields = []) {
	const normalizedItems = Array.isArray(items) ? items : [];

	if (!normalizedItems.length) {
		return [];
	}

	return validateSectionItems(normalizedItems, relationFields);
}

export default function InvoiceEditPage() {
	const {id} = useParams();
	const history = useHistory();
	const {showToast} = useToast();

	const [formState, setFormState] = useState(createInvoiceInitialFormState());
	const [errors, setErrors] = useState({});
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [administrationTitleOptions, setAdministrationTitleOptions] = useState([]);
	const [isLoadingAdministrationTitleOptions, setIsLoadingAdministrationTitleOptions] = useState(false);
	const [doctorTitleOptions, setDoctorTitleOptions] = useState([]);
	const [isLoadingDoctorTitleOptions, setIsLoadingDoctorTitleOptions] = useState(false);
	const [medicineTitleOptions, setMedicineTitleOptions] = useState([]);
	const [isLoadingMedicineTitleOptions, setIsLoadingMedicineTitleOptions] = useState(false);
	const [isLoadingMedicineUnitOptions, setIsLoadingMedicineUnitOptions] = useState(false);
	const [isLoadingMedicinePreparationOptions, setIsLoadingMedicinePreparationOptions] = useState(false);
	const administrationTitleOptionsCacheRef = useRef({});
	const doctorTitleOptionsCacheRef = useRef({});
	const medicineTitleOptionsCacheRef = useRef({});
	const medicineUnitOptionsCacheRef = useRef({});
	const medicinePreparationOptionsCacheRef = useRef({});
	const medicineDetailCacheRef = useRef({});
	const medicineDetailErrorToastRef = useRef({medicineId: null, timestamp: 0});
	const previousHospitalIdRef = useRef("");
	const initialFormStateRef = useRef(createInvoiceInitialFormState());

	const mapAdminPriceRowsToOptions = rows => {
		return (rows || [])
			.map(item => ({
				value: item?.id,
				label: item?.name || item?.title || `Admin Price #${item?.id}`,
				meta: item,
			}))
			.filter(option => option.value !== undefined && option.value !== null);
	};

	const mapDoctorRowsToOptions = rows => {
		return (rows || [])
			.map(item => ({
				value: item?.id,
				label:
					item?.fullName ||
					[item?.firstName, item?.lastName].filter(Boolean).join(" ") ||
					`Doctor #${item?.id}`,
				meta: item,
			}))
			.filter(option => option.value !== undefined && option.value !== null);
	};

	const mapMedicineRowsToOptions = rows => {
		return (rows || [])
			.map(item => ({
				value: item?.id,
				label: item?.name || item?.title || `Medicine #${item?.id}`,
				meta: item,
			}))
			.filter(option => option.value !== undefined && option.value !== null);
	};

	const mapMedicineUnitRowsToOptions = rows => {
		return (rows || [])
			.map(item => ({
				value: item?.id,
				label: item?.name || item?.title || item?.unitName || `Unit #${item?.id}`,
				meta: item,
			}))
			.filter(option => option.value !== undefined && option.value !== null);
	};

	const mapMedicinePreparationRowsToOptions = rows => {
		return (rows || [])
			.map(item => ({
				value: item?.id,
				label: item?.name || item?.title || item?.preparationName || `Sediaan #${item?.id}`,
				meta: item,
			}))
			.filter(option => option.value !== undefined && option.value !== null);
	};

	const buildMedicineRelationCacheKey = (hospitalId, medicineId, search) => {
		return `${hospitalId || ""}::${medicineId || ""}::${search || ""}`;
	};

	const getNormalizedIdValue = value => {
		if (value === undefined || value === null || value === "") {
			return "";
		}

		return value;
	};

	const normalizeSectionItems = useCallback(items => {
		if (!Array.isArray(items)) {
			return [];
		}

		return items.map(item => ({
			id: item?.id ?? null,
			title: item?.title || "",
			qty: item?.qty ?? "",
			price: item?.price ?? "",
			adminPriceId: item?.adminPriceId ?? "",
			doctorUserId: item?.doctorUserId ?? "",
			checkupSupportHospitalId: item?.checkupSupportHospitalId ?? "",
			checkupSupportId: item?.checkupSupportId ?? "",
			medicineId: item?.medicineId ?? "",
			unitId: item?.unitId ?? "",
			sediaanId: item?.sediaanId ?? "",
		}));
	}, []);

	const hasSectionChanged = useCallback(
		sectionKey => {
			const initialItems = normalizeSectionItems(initialFormStateRef.current?.[sectionKey]);
			const currentItems = normalizeSectionItems(formState?.[sectionKey]);

			return JSON.stringify(initialItems) !== JSON.stringify(currentItems);
		},
		[formState, normalizeSectionItems],
	);

	const resolveMedicineRelationFields = async medicineId => {
		if (!medicineId) {
			return {
				unitId: "",
				sediaanId: "",
			};
		}

		if (medicineDetailCacheRef.current?.[medicineId]) {
			return medicineDetailCacheRef.current[medicineId];
		}

		try {
			const detailResponse = await getMedicineDetail(medicineId);
			const detail = detailResponse?.data || detailResponse || {};
			const resolvedFields = {
				unitId: getNormalizedIdValue(detail?.unitId ?? detail?.medicineUnitId ?? detail?.unit?.id),
				sediaanId: getNormalizedIdValue(
					detail?.sediaanId ??
						detail?.medicinePreparationId ??
						detail?.preparationId ??
						detail?.sediaan?.id ??
						detail?.medicinePreparation?.id,
				),
			};

			medicineDetailCacheRef.current[medicineId] = resolvedFields;

			return resolvedFields;
		} catch (error) {
			const now = Date.now();
			const shouldShowToast =
				medicineDetailErrorToastRef.current?.medicineId !== medicineId ||
				now - (medicineDetailErrorToastRef.current?.timestamp || 0) > 10000;

			if (shouldShowToast) {
				showToast(
					"error",
					"Gagal mengambil detail medicine. Pastikan Unit ID dan Sediaan ID terisi sebelum menyimpan.",
					3000,
				);

				medicineDetailErrorToastRef.current = {
					medicineId,
					timestamp: now,
				};
			}

			const emptyResolvedFields = {
				unitId: "",
				sediaanId: "",
			};

			medicineDetailCacheRef.current[medicineId] = emptyResolvedFields;

			return emptyResolvedFields;
		}
	};

	const clearAdministrationOptionsCacheByHospital = hospitalId => {
		if (!hospitalId) {
			return;
		}

		const cacheKeys = Object.keys(administrationTitleOptionsCacheRef.current || {});

		cacheKeys.forEach(cacheKey => {
			if (cacheKey.startsWith(`${hospitalId}::`)) {
				delete administrationTitleOptionsCacheRef.current[cacheKey];
			}
		});
	};

	const clearDoctorOptionsCacheByHospital = hospitalId => {
		if (!hospitalId) {
			return;
		}

		const cacheKeys = Object.keys(doctorTitleOptionsCacheRef.current || {});

		cacheKeys.forEach(cacheKey => {
			if (cacheKey.startsWith(`${hospitalId}::`)) {
				delete doctorTitleOptionsCacheRef.current[cacheKey];
			}
		});
	};

	const clearMedicineOptionsCacheByHospital = hospitalId => {
		if (!hospitalId) {
			return;
		}

		const cacheKeys = Object.keys(medicineTitleOptionsCacheRef.current || {});

		cacheKeys.forEach(cacheKey => {
			if (cacheKey.startsWith(`${hospitalId}::`)) {
				delete medicineTitleOptionsCacheRef.current[cacheKey];
			}
		});
	};

	const clearMedicineRelationOptionsCacheByHospital = hospitalId => {
		if (!hospitalId) {
			return;
		}

		Object.keys(medicineUnitOptionsCacheRef.current || {}).forEach(cacheKey => {
			if (cacheKey.startsWith(`${hospitalId}::`)) {
				delete medicineUnitOptionsCacheRef.current[cacheKey];
			}
		});

		Object.keys(medicinePreparationOptionsCacheRef.current || {}).forEach(cacheKey => {
			if (cacheKey.startsWith(`${hospitalId}::`)) {
				delete medicinePreparationOptionsCacheRef.current[cacheKey];
			}
		});
	};

	const fetchMedicineUnitOptionsByMedicine = useCallback(
		async (medicineId, inputValue = "") => {
			if (!formState?.hospitalId || !medicineId) {
				return [];
			}

			const cacheKey = buildMedicineRelationCacheKey(formState.hospitalId, medicineId, inputValue);
			const cachedOptions = medicineUnitOptionsCacheRef.current?.[cacheKey];

			if (cachedOptions) {
				return cachedOptions;
			}

			setIsLoadingMedicineUnitOptions(true);

			try {
				const response = await getMedicineUnitOptions({
					hospitalId: formState.hospitalId,
					medicineId,
					search: inputValue || undefined,
					limit: 10,
					page: 1,
				});
				const options = mapMedicineUnitRowsToOptions(response?.data?.rows || response?.data || []);

				medicineUnitOptionsCacheRef.current[cacheKey] = options;

				if (!inputValue) {
					medicineUnitOptionsCacheRef.current[
						buildMedicineRelationCacheKey(formState.hospitalId, medicineId, "")
					] = options;
				}

				return options;
			} catch (error) {
				return [];
			} finally {
				setIsLoadingMedicineUnitOptions(false);
			}
		},
		[formState?.hospitalId],
	);

	const fetchMedicinePreparationOptionsByMedicine = useCallback(
		async (medicineId, inputValue = "") => {
			if (!formState?.hospitalId || !medicineId) {
				return [];
			}

			const cacheKey = buildMedicineRelationCacheKey(formState.hospitalId, medicineId, inputValue);
			const cachedOptions = medicinePreparationOptionsCacheRef.current?.[cacheKey];

			if (cachedOptions) {
				return cachedOptions;
			}

			setIsLoadingMedicinePreparationOptions(true);

			try {
				const response = await getMedicinePreparationOptions({
					hospitalId: formState.hospitalId,
					medicineId,
					search: inputValue || undefined,
					limit: 10,
					page: 1,
				});
				const options = mapMedicinePreparationRowsToOptions(response?.data?.rows || response?.data || []);

				medicinePreparationOptionsCacheRef.current[cacheKey] = options;

				if (!inputValue) {
					medicinePreparationOptionsCacheRef.current[
						buildMedicineRelationCacheKey(formState.hospitalId, medicineId, "")
					] = options;
				}

				return options;
			} catch (error) {
				return [];
			} finally {
				setIsLoadingMedicinePreparationOptions(false);
			}
		},
		[formState?.hospitalId],
	);

	const refreshAdministrationTitleOptions = async () => {
		if (!formState?.hospitalId) {
			setAdministrationTitleOptions([]);

			return;
		}

		setIsLoadingAdministrationTitleOptions(true);
		clearAdministrationOptionsCacheByHospital(formState.hospitalId);

		try {
			const response = await getAdminPriceOptions({
				hospitalId: formState.hospitalId,
				limit: 10,
				page: 1,
			});
			const options = mapAdminPriceRowsToOptions(response?.data?.rows || response?.data || []);

			administrationTitleOptionsCacheRef.current[`${formState.hospitalId}::`] = options;
			setAdministrationTitleOptions(options);
		} catch (error) {
			setAdministrationTitleOptions([]);
		} finally {
			setIsLoadingAdministrationTitleOptions(false);
		}
	};

	const refreshDoctorTitleOptions = async () => {
		if (!formState?.hospitalId) {
			setDoctorTitleOptions([]);

			return;
		}

		setIsLoadingDoctorTitleOptions(true);
		clearDoctorOptionsCacheByHospital(formState.hospitalId);

		try {
			const response = await getDoctorOptions({
				hospitalId: formState.hospitalId,
				limit: 10,
				page: 1,
			});
			const options = mapDoctorRowsToOptions(response?.data?.rows || response?.data || []);

			doctorTitleOptionsCacheRef.current[`${formState.hospitalId}::`] = options;
			setDoctorTitleOptions(options);
		} catch (error) {
			setDoctorTitleOptions([]);
		} finally {
			setIsLoadingDoctorTitleOptions(false);
		}
	};

	const refreshMedicineTitleOptions = async () => {
		if (!formState?.hospitalId) {
			setMedicineTitleOptions([]);

			return;
		}

		setIsLoadingMedicineTitleOptions(true);
		clearMedicineOptionsCacheByHospital(formState.hospitalId);

		try {
			const response = await getMedicineOptions({
				hospitalId: formState.hospitalId,
				limit: 10,
				page: 1,
			});
			const options = mapMedicineRowsToOptions(response?.data?.rows || response?.data || []);

			medicineTitleOptionsCacheRef.current[`${formState.hospitalId}::`] = options;
			setMedicineTitleOptions(options);
		} catch (error) {
			setMedicineTitleOptions([]);
		} finally {
			setIsLoadingMedicineTitleOptions(false);
		}
	};

	useEffect(() => {
		let isMounted = true;

		async function fetchDetail() {
			setLoading(true);

			try {
				const response = await getInvoiceDetail(id);

				if (!isMounted) {
					return;
				}

				const mappedFormState = mapInvoiceDetailToFormState(response);

				setFormState(mappedFormState);
				initialFormStateRef.current = mappedFormState;
			} catch (apiError) {
				if (!isMounted) {
					return;
				}

				showToast("error", getApiErrorMessage(apiError), 3000);
				history.push("/invoice-management");
			} finally {
				if (isMounted) {
					setLoading(false);
				}
			}
		}

		fetchDetail();

		return () => {
			isMounted = false;
		};
	}, [history, id, showToast]);

	useEffect(() => {
		const previousHospitalId = previousHospitalIdRef.current;
		const currentHospitalId = formState?.hospitalId;

		if (previousHospitalId && currentHospitalId && previousHospitalId !== currentHospitalId) {
			clearAdministrationOptionsCacheByHospital(previousHospitalId);
			clearDoctorOptionsCacheByHospital(previousHospitalId);
			clearMedicineOptionsCacheByHospital(previousHospitalId);
			clearMedicineRelationOptionsCacheByHospital(previousHospitalId);
			medicineDetailCacheRef.current = {};
		}

		previousHospitalIdRef.current = currentHospitalId || "";
	}, [formState?.hospitalId]);

	useEffect(() => {
		let isMounted = true;

		async function fetchDefaultDoctorOptions() {
			if (!formState?.hospitalId) {
				if (isMounted) {
					setIsLoadingDoctorTitleOptions(false);
					setDoctorTitleOptions([]);
				}

				return;
			}

			if (isMounted) {
				setIsLoadingDoctorTitleOptions(true);
			}

			const cacheKey = `${formState.hospitalId}::`;
			const cachedOptions = doctorTitleOptionsCacheRef.current?.[cacheKey];

			if (cachedOptions) {
				if (isMounted) {
					setDoctorTitleOptions(cachedOptions);
					setIsLoadingDoctorTitleOptions(false);
				}

				return;
			}

			try {
				const response = await getDoctorOptions({
					hospitalId: formState.hospitalId,
					limit: 10,
					page: 1,
				});

				if (!isMounted) {
					return;
				}

				const options = mapDoctorRowsToOptions(response?.data?.rows || response?.data || []);

				doctorTitleOptionsCacheRef.current[cacheKey] = options;
				setDoctorTitleOptions(options);
				setIsLoadingDoctorTitleOptions(false);
			} catch (error) {
				if (isMounted) {
					setIsLoadingDoctorTitleOptions(false);
					setDoctorTitleOptions([]);
				}
			}
		}

		fetchDefaultDoctorOptions();

		return () => {
			isMounted = false;
		};
	}, [formState?.hospitalId]);

	useEffect(() => {
		let isMounted = true;

		async function fetchDefaultMedicineOptions() {
			if (!formState?.hospitalId) {
				if (isMounted) {
					setIsLoadingMedicineTitleOptions(false);
					setMedicineTitleOptions([]);
				}

				return;
			}

			if (isMounted) {
				setIsLoadingMedicineTitleOptions(true);
			}

			const cacheKey = `${formState.hospitalId}::`;
			const cachedOptions = medicineTitleOptionsCacheRef.current?.[cacheKey];

			if (cachedOptions) {
				if (isMounted) {
					setMedicineTitleOptions(cachedOptions);
					setIsLoadingMedicineTitleOptions(false);
				}

				return;
			}

			try {
				const response = await getMedicineOptions({
					hospitalId: formState.hospitalId,
					limit: 10,
					page: 1,
				});

				if (!isMounted) {
					return;
				}

				const options = mapMedicineRowsToOptions(response?.data?.rows || response?.data || []);

				medicineTitleOptionsCacheRef.current[cacheKey] = options;
				setMedicineTitleOptions(options);
				setIsLoadingMedicineTitleOptions(false);
			} catch (error) {
				if (isMounted) {
					setIsLoadingMedicineTitleOptions(false);
					setMedicineTitleOptions([]);
				}
			}
		}

		fetchDefaultMedicineOptions();

		return () => {
			isMounted = false;
		};
	}, [formState?.hospitalId]);

	useEffect(() => {
		let isMounted = true;

		async function fetchDefaultAdminPriceOptions() {
			if (!formState?.hospitalId) {
				if (isMounted) {
					setIsLoadingAdministrationTitleOptions(false);
					setAdministrationTitleOptions([]);
				}

				return;
			}

			if (isMounted) {
				setIsLoadingAdministrationTitleOptions(true);
			}

			const cacheKey = `${formState.hospitalId}::`;
			const cachedOptions = administrationTitleOptionsCacheRef.current?.[cacheKey];

			if (cachedOptions) {
				if (isMounted) {
					setAdministrationTitleOptions(cachedOptions);
					setIsLoadingAdministrationTitleOptions(false);
				}

				return;
			}

			try {
				const response = await getAdminPriceOptions({
					hospitalId: formState.hospitalId,
					limit: 10,
					page: 1,
				});

				if (!isMounted) {
					return;
				}

				const options = mapAdminPriceRowsToOptions(response?.data?.rows || response?.data || []);

				administrationTitleOptionsCacheRef.current[cacheKey] = options;

				setAdministrationTitleOptions(options);
				setIsLoadingAdministrationTitleOptions(false);
			} catch (error) {
				if (isMounted) {
					setIsLoadingAdministrationTitleOptions(false);
					setAdministrationTitleOptions([]);
				}
			}
		}

		fetchDefaultAdminPriceOptions();

		return () => {
			isMounted = false;
		};
	}, [formState?.hospitalId]);

	const fetchAdministrationTitleOptions = async (inputValue, callback) => {
		if (!formState?.hospitalId) {
			callback([]);

			return;
		}

		try {
			const cacheKey = `${formState.hospitalId}::${inputValue || ""}`;
			const cachedOptions = administrationTitleOptionsCacheRef.current?.[cacheKey];

			if (cachedOptions) {
				callback(cachedOptions);

				return;
			}

			const response = await getAdminPriceOptions({
				hospitalId: formState.hospitalId,
				search: inputValue || undefined,
				limit: 10,
				page: 1,
			});
			const options = mapAdminPriceRowsToOptions(response?.data?.rows || response?.data || []);

			administrationTitleOptionsCacheRef.current[cacheKey] = options;

			if (!inputValue) {
				administrationTitleOptionsCacheRef.current[`${formState.hospitalId}::`] = options;
			}

			callback(options);
		} catch (error) {
			callback([]);
		}
	};

	const fetchDoctorTitleOptions = async (inputValue, callback) => {
		if (!formState?.hospitalId) {
			callback([]);

			return;
		}

		try {
			const cacheKey = `${formState.hospitalId}::${inputValue || ""}`;
			const cachedOptions = doctorTitleOptionsCacheRef.current?.[cacheKey];

			if (cachedOptions) {
				callback(cachedOptions);

				return;
			}

			const response = await getDoctorOptions({
				hospitalId: formState.hospitalId,
				search: inputValue || undefined,
				limit: 10,
				page: 1,
			});
			const options = mapDoctorRowsToOptions(response?.data?.rows || response?.data || []);

			doctorTitleOptionsCacheRef.current[cacheKey] = options;

			if (!inputValue) {
				doctorTitleOptionsCacheRef.current[`${formState.hospitalId}::`] = options;
			}

			callback(options);
		} catch (error) {
			callback([]);
		}
	};

	const fetchMedicineTitleOptions = async (inputValue, callback) => {
		if (!formState?.hospitalId) {
			callback([]);

			return;
		}

		try {
			const cacheKey = `${formState.hospitalId}::${inputValue || ""}`;
			const cachedOptions = medicineTitleOptionsCacheRef.current?.[cacheKey];

			if (cachedOptions) {
				callback(cachedOptions);

				return;
			}

			const response = await getMedicineOptions({
				hospitalId: formState.hospitalId,
				search: inputValue || undefined,
				limit: 10,
				page: 1,
			});
			const options = mapMedicineRowsToOptions(response?.data?.rows || response?.data || []);

			medicineTitleOptionsCacheRef.current[cacheKey] = options;

			if (!inputValue) {
				medicineTitleOptionsCacheRef.current[`${formState.hospitalId}::`] = options;
			}

			callback(options);
		} catch (error) {
			callback([]);
		}
	};

	useEffect(() => {
		if (!formState?.hospitalId) {
			return;
		}

		const medicineIds = Array.from(
			new Set((formState?.medicines || []).map(item => item?.medicineId).filter(Boolean)),
		);

		if (!medicineIds.length) {
			return;
		}

		medicineIds.forEach(medicineId => {
			const unitCacheKey = buildMedicineRelationCacheKey(formState.hospitalId, medicineId, "");
			const preparationCacheKey = buildMedicineRelationCacheKey(formState.hospitalId, medicineId, "");

			if (
				medicineUnitOptionsCacheRef.current?.[unitCacheKey] &&
				medicinePreparationOptionsCacheRef.current?.[preparationCacheKey]
			) {
				return;
			}

			void Promise.all([
				fetchMedicineUnitOptionsByMedicine(medicineId, ""),
				fetchMedicinePreparationOptionsByMedicine(medicineId, ""),
			]);
		});
	}, [
		fetchMedicinePreparationOptionsByMedicine,
		fetchMedicineUnitOptionsByMedicine,
		formState?.hospitalId,
		formState?.medicines,
	]);

	const fetchMedicineUnitRelationOptions = async (inputValue, callback, item) => {
		if (!item?.medicineId) {
			callback([]);

			return;
		}

		const options = await fetchMedicineUnitOptionsByMedicine(item.medicineId, inputValue || "");

		callback(options);
	};

	const fetchMedicinePreparationRelationOptions = async (inputValue, callback, item) => {
		if (!item?.medicineId) {
			callback([]);

			return;
		}

		const options = await fetchMedicinePreparationOptionsByMedicine(item.medicineId, inputValue || "");

		callback(options);
	};

	const totals = useMemo(
		() => ({
			administrations: calculateSectionSubtotal(formState.administrations),
			doctorPrices: calculateSectionSubtotal(formState.doctorPrices),
			checkupSupports: calculateSectionSubtotal(formState.checkupSupports),
			medicines: calculateSectionSubtotal(formState.medicines),
			grandTotal: calculateGrandTotal(formState),
		}),
		[formState],
	);

	const validateForm = () => {
		const nextErrors = {};

		if (!formState?.date) {
			nextErrors.date = "Tanggal wajib diisi";
		}

		if (!formState?.paymentMethod) {
			nextErrors.paymentMethod = "Payment method wajib diisi";
		}

		if (formState?.paymentMethod === "custom" && !formState?.customPaymentMethodId) {
			nextErrors.customPaymentMethodId = "Custom payment method ID wajib diisi";
		}

		if (formState?.paymentMethod === "multiple_payment") {
			const rawCashAmount = formState?.cashAmount;
			const cashAmount = toPositiveNumber(rawCashAmount);

			if (rawCashAmount === "" || rawCashAmount === null || rawCashAmount === undefined || cashAmount < 0) {
				nextErrors.cashAmount = "Cash amount harus angka dan >= 0";
			}

			if (formState?.additionalPaymentMethod !== "share_link") {
				nextErrors.additionalPaymentMethod = "Additional payment method harus share_link";
			}
		}

		nextErrors.administrations = hasSectionChanged("administrations")
			? validateOptionalSectionItems(formState?.administrations, INVOICE_PRODUCT_RELATION_FIELDS.administrations)
			: [];
		nextErrors.doctorPrices = hasSectionChanged("doctorPrices")
			? validateOptionalSectionItems(formState?.doctorPrices, INVOICE_PRODUCT_RELATION_FIELDS.doctorPrices)
			: [];
		nextErrors.checkupSupports = hasSectionChanged("checkupSupports")
			? validateOptionalSectionItems(formState?.checkupSupports, INVOICE_PRODUCT_RELATION_FIELDS.checkupSupports)
			: [];
		nextErrors.medicines = hasSectionChanged("medicines")
			? validateOptionalSectionItems(formState?.medicines, INVOICE_PRODUCT_RELATION_FIELDS.medicines)
			: [];

		setErrors(nextErrors);

		const hasHeaderError = Boolean(
			nextErrors.date ||
				nextErrors.paymentMethod ||
				nextErrors.customPaymentMethodId ||
				nextErrors.cashAmount ||
				nextErrors.additionalPaymentMethod,
		);

		const hasSectionError = ["administrations", "doctorPrices", "checkupSupports", "medicines"].some(section =>
			nextErrors?.[section]?.some(itemError => Object.keys(itemError).length > 0),
		);

		return !(hasHeaderError || hasSectionError);
	};

	const handleFieldChange = (field, value) => {
		const nextValue = field === "customPaymentMethodId" && typeof value === "string" ? value.trim() : value;

		setFormState(current => {
			const nextState = {
				...current,
				[field]: nextValue,
			};

			if (field === "paymentMethod") {
				nextState.customPaymentMethodId = "";
				nextState.customPaymentMethodName = "";
				nextState.cashAmount = "";
				nextState.additionalPaymentMethod = value === "multiple_payment" ? "share_link" : "";
			}

			if (field === "additionalPaymentMethod") {
				nextState.additionalPaymentMethod = "share_link";
			}

			return nextState;
		});
	};

	const handleSubmit = async event => {
		event.preventDefault();

		if (!validateForm()) {
			showToast("error", "Mohon periksa kembali form yang belum valid", 3000);

			return;
		}

		setSubmitting(true);

		try {
			const payload = mapFormStateToUpdatePayload(formState);
			const doctorResolveCache = {};
			const isDoctorSectionChanged = hasSectionChanged("doctorPrices");

			payload.administrations = Array.isArray(payload?.administrations) ? payload.administrations : [];
			payload.doctorPrices = Array.isArray(payload?.doctorPrices) ? payload.doctorPrices : [];
			payload.checkupSupports = Array.isArray(payload?.checkupSupports) ? payload.checkupSupports : [];
			payload.medicines = Array.isArray(payload?.medicines) ? payload.medicines : [];

			if (payload.doctorPrices.length) {
				const resolvedDoctorPrices = await Promise.all(
					payload.doctorPrices.map(async item => {
						if (
							item?.doctorUserId !== "" &&
							item?.doctorUserId !== null &&
							item?.doctorUserId !== undefined
						) {
							return item;
						}

						const normalizedTitle = (item?.title || "").trim().toLowerCase();

						if (!normalizedTitle || !formState?.hospitalId) {
							return isDoctorSectionChanged ? item : null;
						}

						if (doctorResolveCache[normalizedTitle] !== undefined) {
							const cachedDoctorUserId = doctorResolveCache[normalizedTitle];

							if (!cachedDoctorUserId && !isDoctorSectionChanged) {
								return null;
							}

							return {
								...item,
								doctorUserId: cachedDoctorUserId || item?.doctorUserId,
							};
						}

						try {
							const response = await getDoctorOptions({
								hospitalId: formState.hospitalId,
								search: item?.title || undefined,
								limit: 10,
								page: 1,
							});
							const options = mapDoctorRowsToOptions(response?.data?.rows || response?.data || []);
							const exactMatch = options.find(
								option => (option?.label || "").trim().toLowerCase() === normalizedTitle,
							);
							const partialMatch = options.find(option =>
								(option?.label || "").trim().toLowerCase().includes(normalizedTitle),
							);
							const resolvedDoctorUserId = exactMatch?.value || partialMatch?.value || "";

							doctorResolveCache[normalizedTitle] = resolvedDoctorUserId;

							if (!resolvedDoctorUserId && !isDoctorSectionChanged) {
								return null;
							}

							return {
								...item,
								doctorUserId: resolvedDoctorUserId || item?.doctorUserId,
							};
						} catch (error) {
							if (!isDoctorSectionChanged) {
								return null;
							}

							return item;
						}
					}),
				);

				payload.doctorPrices = resolvedDoctorPrices.filter(Boolean);
			}

			await updateInvoice(id, payload);
			showToast("success", "Invoice berhasil diperbarui", 3000);
			history.push(`/invoice-management?refresh=${Date.now()}`);
		} catch (apiError) {
			showToast("error", getApiErrorMessage(apiError), 3000);
		} finally {
			setSubmitting(false);
		}
	};

	const administrationTitleAutocomplete = {
		disabled: !formState?.hospitalId,
		label: "Cari admin price...",
		defaultOptions: administrationTitleOptions,
		fetchOptions: fetchAdministrationTitleOptions,
		isLoading: isLoadingAdministrationTitleOptions,
		isRefreshing: isLoadingAdministrationTitleOptions,
		loadingMessage: () => "Memuat admin price...",
		noOptionsMessage: () =>
			formState?.hospitalId ? "Tidak ada admin price untuk rumah sakit ini" : "Hospital ID tidak tersedia",
		onRefresh: refreshAdministrationTitleOptions,
		getOptionFromItem: item => {
			if (!item?.title && !item?.adminPriceId) {
				return null;
			}

			return {
				value: item?.adminPriceId || item?.id,
				label: item?.title || `Admin Price #${item?.adminPriceId}`,
			};
		},
		mapOptionToItem: (option, currentItem) => ({
			...currentItem,
			title: option?.label || "",
			adminPriceId: option?.value || "",
			price: option?.meta?.price !== undefined ? option.meta.price : option ? currentItem?.price : "",
		}),
	};

	const doctorTitleAutocomplete = {
		disabled: !formState?.hospitalId,
		label: "Cari dokter...",
		defaultOptions: doctorTitleOptions,
		fetchOptions: fetchDoctorTitleOptions,
		isLoading: isLoadingDoctorTitleOptions,
		isRefreshing: isLoadingDoctorTitleOptions,
		loadingMessage: () => "Memuat data dokter...",
		noOptionsMessage: () =>
			formState?.hospitalId ? "Tidak ada dokter untuk rumah sakit ini" : "Hospital ID tidak tersedia",
		onRefresh: refreshDoctorTitleOptions,
		getOptionFromItem: item => {
			if (!item?.title && !item?.doctorUserId) {
				return null;
			}

			return {
				value: item?.doctorUserId,
				label: item?.title || `Doctor #${item?.doctorUserId}`,
			};
		},
		mapOptionToItem: (option, currentItem) => ({
			...currentItem,
			title: option?.label || "",
			doctorUserId: option?.value || "",
		}),
	};

	const medicineTitleAutocomplete = {
		disabled: !formState?.hospitalId,
		label: "Cari medicine...",
		defaultOptions: medicineTitleOptions,
		fetchOptions: fetchMedicineTitleOptions,
		isLoading: isLoadingMedicineTitleOptions,
		isRefreshing: isLoadingMedicineTitleOptions,
		loadingMessage: () => "Memuat data medicine...",
		noOptionsMessage: () =>
			formState?.hospitalId ? "Tidak ada medicine untuk rumah sakit ini" : "Hospital ID tidak tersedia",
		onRefresh: refreshMedicineTitleOptions,
		getOptionFromItem: item => {
			if (!item?.title && !item?.medicineId) {
				return null;
			}

			return {
				value: item?.medicineId,
				label: item?.title || `Medicine #${item?.medicineId}`,
			};
		},
		mapOptionToItem: async (option, currentItem) => {
			if (!option) {
				return {
					...currentItem,
					title: "",
					medicineId: "",
					unitId: "",
					sediaanId: "",
				};
			}

			const selectedMedicineId = option?.value || "";
			const optionUnitId = getNormalizedIdValue(
				option?.meta?.unitId ?? option?.meta?.medicineUnitId ?? option?.meta?.unit?.id,
			);
			const optionSediaanId = getNormalizedIdValue(
				option?.meta?.sediaanId ??
					option?.meta?.medicinePreparationId ??
					option?.meta?.preparationId ??
					option?.meta?.sediaan?.id,
			);

			let resolvedUnitId = optionUnitId;
			let resolvedSediaanId = optionSediaanId;

			if (!resolvedUnitId || !resolvedSediaanId) {
				const fallbackFields = await resolveMedicineRelationFields(selectedMedicineId);

				resolvedUnitId = resolvedUnitId || fallbackFields.unitId || currentItem?.unitId || "";
				resolvedSediaanId = resolvedSediaanId || fallbackFields.sediaanId || currentItem?.sediaanId || "";
			}

			void Promise.all([
				fetchMedicineUnitOptionsByMedicine(selectedMedicineId, ""),
				fetchMedicinePreparationOptionsByMedicine(selectedMedicineId, ""),
			]);

			return {
				...currentItem,
				title: option?.label || "",
				medicineId: selectedMedicineId,
				unitId: resolvedUnitId,
				sediaanId: resolvedSediaanId,
			};
		},
	};

	const medicineRelationFieldAutocompletes = {
		unitId: {
			disabled: item => !formState?.hospitalId || !item?.medicineId,
			label: "Cari unit...",
			defaultOptions: item => {
				if (!formState?.hospitalId || !item?.medicineId) {
					return [];
				}

				return (
					medicineUnitOptionsCacheRef.current?.[
						buildMedicineRelationCacheKey(formState.hospitalId, item.medicineId, "")
					] || []
				);
			},
			fetchOptions: fetchMedicineUnitRelationOptions,
			isLoading: isLoadingMedicineUnitOptions,
			loadingMessage: () => "Memuat unit...",
			noOptionsMessage: () => "Tidak ada unit untuk medicine ini",
			getOptionFromItem: item => {
				if (!item?.unitId) {
					return null;
				}

				const defaultOptions = formState?.hospitalId
					? medicineUnitOptionsCacheRef.current?.[
							buildMedicineRelationCacheKey(formState.hospitalId, item?.medicineId, "")
					  ] || []
					: [];
				const matchedOption = defaultOptions.find(option => String(option?.value) === String(item?.unitId));

				return matchedOption || {value: item?.unitId, label: `Unit #${item?.unitId}`};
			},
			mapOptionToItem: (option, currentItem) => ({
				...currentItem,
				unitId: option?.value || "",
			}),
		},
		sediaanId: {
			disabled: item => !formState?.hospitalId || !item?.medicineId,
			label: "Cari sediaan...",
			defaultOptions: item => {
				if (!formState?.hospitalId || !item?.medicineId) {
					return [];
				}

				return (
					medicinePreparationOptionsCacheRef.current?.[
						buildMedicineRelationCacheKey(formState.hospitalId, item.medicineId, "")
					] || []
				);
			},
			fetchOptions: fetchMedicinePreparationRelationOptions,
			isLoading: isLoadingMedicinePreparationOptions,
			loadingMessage: () => "Memuat sediaan...",
			noOptionsMessage: () => "Tidak ada sediaan untuk medicine ini",
			getOptionFromItem: item => {
				if (!item?.sediaanId) {
					return null;
				}

				const defaultOptions = formState?.hospitalId
					? medicinePreparationOptionsCacheRef.current?.[
							buildMedicineRelationCacheKey(formState.hospitalId, item?.medicineId, "")
					  ] || []
					: [];
				const matchedOption = defaultOptions.find(option => String(option?.value) === String(item?.sediaanId));

				return matchedOption || {value: item?.sediaanId, label: `Sediaan #${item?.sediaanId}`};
			},
			mapOptionToItem: (option, currentItem) => ({
				...currentItem,
				sediaanId: option?.value || "",
			}),
		},
	};

	const doctorVisibleRelationFields = [];
	const medicineVisibleRelationFields = INVOICE_PRODUCT_RELATION_FIELDS.medicines.filter(
		relationField => relationField.key !== "medicineId",
	);

	if (loading) {
		return (
			<DashboardLayout>
				<div className="box-white ws p24">
					<CircleLoader />
				</div>
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout>
			<form onSubmit={handleSubmit}>
				<div className="d-flex justify-content-between align-items-center mb-4">
					<div className="text-bold text-dark">Edit Invoice</div>
					<button
						className="btn btn-outline-secondary"
						type="button"
						onClick={() => history.push("/invoice-management")}>
						Kembali
					</button>
				</div>

				<div className="box-white ws p24 mb-3">
					<div className="ttl-20 mb-3">Invoice Info</div>
					<div className="row g-2">
						<div className="col-md-4">
							<label className="setting-label">Invoice Number</label>
							<input
								disabled
								className="form-control"
								type="text"
								value={formState?.invoiceNumber || "-"}
							/>
						</div>
						<div className="col-md-4">
							<label className="setting-label">Patient Name</label>
							<input
								disabled
								className="form-control"
								type="text"
								value={formState?.patientName || "-"}
							/>
						</div>
						<div className="col-md-4">
							<label className="setting-label">Hospital Name</label>
							<input
								disabled
								className="form-control"
								type="text"
								value={formState?.hospitalName || "-"}
							/>
						</div>
						<div className="col-md-4">
							<label className="setting-label required">Date</label>
							<input
								required
								className="form-control"
								type="date"
								value={formState?.date || ""}
								onChange={event => handleFieldChange("date", event.target.value)}
							/>
							{errors?.date ? <p className="form-error-item-message">{errors.date}</p> : null}
						</div>
					</div>
				</div>

				<PaymentMethodFields errors={errors} formState={formState} onFieldChange={handleFieldChange} />

				<InvoiceProductSection
					errorItems={errors?.administrations}
					items={formState?.administrations}
					relationFields={INVOICE_PRODUCT_RELATION_FIELDS.administrations}
					title="Administrations"
					titleAutocomplete={administrationTitleAutocomplete}
					onChange={nextItems => handleFieldChange("administrations", nextItems)}
				/>
				<InvoiceProductSection
					errorItems={errors?.doctorPrices}
					items={formState?.doctorPrices}
					relationFields={doctorVisibleRelationFields}
					title="Doctor Prices"
					titleAutocomplete={doctorTitleAutocomplete}
					onChange={nextItems => handleFieldChange("doctorPrices", nextItems)}
				/>
				<InvoiceProductSection
					errorItems={errors?.checkupSupports}
					items={formState?.checkupSupports}
					relationFields={INVOICE_PRODUCT_RELATION_FIELDS.checkupSupports}
					title="Checkup Supports"
					onChange={nextItems => handleFieldChange("checkupSupports", nextItems)}
				/>
				<InvoiceProductSection
					errorItems={errors?.medicines}
					items={formState?.medicines}
					relationFieldAutocompletes={medicineRelationFieldAutocompletes}
					relationFields={medicineVisibleRelationFields}
					title="Medicines"
					titleAutocomplete={medicineTitleAutocomplete}
					onChange={nextItems => handleFieldChange("medicines", nextItems)}
				/>

				<div className="box-white ws p24 mb-3">
					<div className="ttl-20 mb-3">Total Preview</div>
					<div className="d-flex justify-content-between mb-2">
						<span>Administrations</span>
						<span>{formatCurrency(totals.administrations)}</span>
					</div>
					<div className="d-flex justify-content-between mb-2">
						<span>Doctor Prices</span>
						<span>{formatCurrency(totals.doctorPrices)}</span>
					</div>
					<div className="d-flex justify-content-between mb-2">
						<span>Checkup Supports</span>
						<span>{formatCurrency(totals.checkupSupports)}</span>
					</div>
					<div className="d-flex justify-content-between mb-2">
						<span>Medicines</span>
						<span>{formatCurrency(totals.medicines)}</span>
					</div>
					<hr />
					<div className="d-flex justify-content-between text-bold">
						<span>Grand Total</span>
						<span>{formatCurrency(totals.grandTotal)}</span>
					</div>
				</div>

				<div className="text-center my-3">
					<button className="btn btn-orange d-inline-block mw-250" disabled={submitting} type="submit">
						{submitting ? "Menyimpan..." : "Simpan Perubahan"}
					</button>
				</div>
			</form>
		</DashboardLayout>
	);
}
